import { useState } from 'react';
import { Megaphone, Radio, Share2, Store } from 'lucide-react';

import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { ProPill } from '@/components/ui/pro-pill';
import { ChannelCard } from '@/components/channels/ChannelCard';
import { LiveChannelCard } from '@/components/channels/LiveChannelCard';
import { LaunchStorefrontModal, markStorefrontLaunched } from '@/components/lifecycle/LaunchStorefrontModal';
import { useShop } from '@/hooks/useShop';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useVerifyEmail } from '@/components/verification/VerifyEmailProvider';
import { useTier } from '@/lib/tier';
import { buildCatalogue } from '@/lib/channels';
import { cn } from '@/lib/utils';
import { PageScore } from '@/components/overview/LocalPotentialHeader';

const HEADER_SUB =
	"Choose where your products are shown online — every channel that's automatically promoting your in-store stock to local shoppers.";

function ChannelsLoading() {
	return (
		<div className="space-y-12">
			<header>
				<Skeleton className="h-9 w-48" />
				<Skeleton className="mt-3 h-5 w-full max-w-2xl" />
			</header>
			{[0, 1].map((section) => (
				<section key={section} className="space-y-4">
					<Skeleton className="h-6 w-56" />
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
						{[0, 1, 2].map((card) => (
							<Skeleton key={card} className="h-44" />
						))}
					</div>
				</section>
			))}
		</div>
	);
}

/** Pro allowance banner — shown only on the Pro tier. */
function ProBanner({ slotsUsed, slotsLimit }) {
	const detail =
		slotsUsed === 0
			? "Pick any 2 paid channels — they're on us. Additional channels are charged as listed."
			: slotsUsed >= slotsLimit
				? "You're using both included slots. Additional channels are charged as listed."
				: `Using ${slotsUsed} of ${slotsLimit} included slots. One more on us.`;
	return (
		<div className="flex flex-wrap items-center gap-4 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-card to-action/5 p-4 md:p-5">
			<ProPill className="shrink-0 px-2.5 py-1 text-xs" />
			<div className="min-w-0 flex-1">
				<div className="text-sm font-bold text-foreground">Pro includes 2 paid channels free</div>
				<div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>
			</div>
			<div className="flex items-center gap-1.5">
				{Array.from({ length: slotsLimit }).map((_, index) => (
					<span key={index} className={cn('h-2 w-8 rounded-full', index < slotsUsed ? 'bg-primary' : 'bg-primary/20')} />
				))}
				<span className="ml-2 text-xs font-semibold tabular-nums text-foreground">
					{slotsUsed}/{slotsLimit}
				</span>
			</div>
		</div>
	);
}

const categoryFilters = (catalogue) => [
	{ key: 'all', label: 'All' },
	...catalogue.map((group) => ({ key: group.key, label: group.filterLabel }))
];

export function Channels() {
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { billing } = useBillingStatus(shop?.id);
	const { enable } = useChannelActions(shop?.id);
	const { requireVerifiedEmail } = useVerifyEmail();
	const { isPro, slotsUsed, slotsLimit, includedSlugs, slotsAvailable } = useTier();
	const [showAll, setShowAll] = useState(false);
	const [activeCategory, setActiveCategory] = useState('all');
	const [launchOpen, setLaunchOpen] = useState(false);

	const loading = !shop?.id || channelsLoading || defsLoading;
	if (loading) return <ChannelsLoading />;

	// `buildCatalogue` falls back gracefully when entitlements haven't resolved
	// yet — prices appear as soon as billing-status returns. We deliberately
	// don't block the whole page on billing-status (1s+ Stripe round trip).
	const catalogue = buildCatalogue(definitions, channels, billing?.entitlements, shop?.currency);
	// Google mid-setup (`settingUp`) sits alongside the live channels — it's
	// auto-provisioned, so the discovery "Learn more"/enable CTAs never apply.
	const liveChannels = catalogue.flatMap((group) => group.channels.filter((channel) => channel.live || channel.settingUp));
	const anyLive = liveChannels.length > 0;
	// Auto-provisioned channels (Google) don't count as something the retailer
	// has switched on — a brand-new shop still sees the first-channel welcome.
	const anyChannelSwitchedOn = liveChannels.some((channel) => channel.live && !channel.autoProvisioned);

	const discoveryGroups = catalogue
		.map((group) => ({ ...group, channels: group.channels.filter((channel) => !channel.live && !channel.settingUp) }))
		.filter((group) => group.channels.length > 0);
	const filters = categoryFilters(discoveryGroups);
	const filteredGroups = activeCategory === 'all' ? discoveryGroups : discoveryGroups.filter((group) => group.key === activeCategory);

	// First-login: the retailer hasn't switched on a channel and hasn't asked
	// to browse the full catalogue yet → the welcome empty state.
	const showWelcome = !anyChannelSwitchedOn && !showAll;

	return (
		<>
			{showWelcome ? (
				<div className="space-y-10">
					<header className="flex flex-wrap items-start justify-between gap-4">
						<div>
							<h1 className="text-3xl font-extrabold tracking-tight md:text-[34px]">Channels</h1>
							<p className="mt-2 max-w-2xl text-base text-muted-foreground">{HEADER_SUB}</p>
						</div>
						<div className="ml-auto shrink-0">
							<PageScore />
						</div>
					</header>
					<EmptyState
						icon={Radio}
						eyebrow="One catalogue, everywhere"
						title="Switch on your first channel in minutes"
						body="Pick where you want to show up — Google, Meta, your own storefront — and we'll keep your stock in sync automatically. No manual uploads. Ever."
						primaryCta="Launch my Local Storefront"
						onPrimaryClick={() => requireVerifiedEmail(() => setLaunchOpen(true))}
						secondaryCta="Browse all channels"
						onSecondaryClick={() => setShowAll(true)}
						previewTitle="Popular places to start"
						preview={[
							{ icon: Store, label: 'Google Local Listings', detail: 'Free — show up when locals search nearby' },
							{ icon: Megaphone, label: 'Local Inventory Ads', detail: 'Paid boost — promote products to local shoppers' },
							{ icon: Share2, label: 'Meta Commerce', detail: 'Sell on Facebook & Instagram' }
						]}
					/>
				</div>
			) : (
				<div className="space-y-12">
					<header className="flex flex-wrap items-start justify-between gap-4">
						<div>
							<h1 className="text-3xl font-extrabold tracking-tight md:text-[34px]">Channels</h1>
							<p className="mt-2 max-w-2xl text-base text-muted-foreground">{HEADER_SUB}</p>
						</div>
						<div className="ml-auto shrink-0">
							<PageScore />
						</div>
					</header>

					{isPro ? <ProBanner slotsUsed={slotsUsed} slotsLimit={slotsLimit} /> : null}

					{anyLive ? (
						<section>
							<SectionHeader
								eyebrow="Live"
								title="Manage your channels"
								sub="Manage what's currently promoting your stock."
							/>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
								{liveChannels.map((channel) => (
									<LiveChannelCard key={channel.id} channel={channel} />
								))}
							</div>
						</section>
					) : null}

					{discoveryGroups.length > 0 ? (
						<section className="space-y-8">
							<SectionHeader
								eyebrow="Grow"
								title="Expand your local reach"
								sub="Turn on more channels to put your in-store stock in front of more local shoppers."
							/>
							<div className="-mt-4 flex gap-2 overflow-x-auto pb-1">
								{filters.map((filter) => {
									const count =
										filter.key === 'all'
											? discoveryGroups.reduce((total, group) => total + group.channels.length, 0)
											: (discoveryGroups.find((group) => group.key === filter.key)?.channels.length ?? 0);
									const isActive = activeCategory === filter.key;
									return (
										<button
											key={filter.key}
											type="button"
											onClick={() => setActiveCategory(filter.key)}
											className={cn(
												'shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition',
												isActive
													? 'border-foreground bg-foreground text-background'
													: 'border-border bg-accent text-foreground hover:bg-accent/80'
											)}
										>
											{filter.label}
											<span
												className={cn(
													'ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
													isActive
														? 'bg-background/20 text-background'
														: 'border border-border bg-background text-muted-foreground'
												)}
											>
												{count}
											</span>
										</button>
									);
								})}
							</div>
							<div className="min-h-[640px] space-y-10">
								{filteredGroups.map((group) => (
									<section key={group.key} className="space-y-4">
										<div>
											<h3 className="text-base font-bold text-foreground">{group.title}</h3>
											<p className="mt-1 text-sm text-muted-foreground">{group.description}</p>
										</div>
										<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
											{group.channels.map((channel) => (
												<ChannelCard
													key={channel.id}
													channel={channel}
													isPro={isPro}
													claimed={includedSlugs.includes(channel.id)}
													slotsAvailable={slotsAvailable}
												/>
											))}
										</div>
									</section>
								))}
							</div>
						</section>
					) : null}
				</div>
			)}

			<LaunchStorefrontModal
				open={launchOpen}
				onClose={() => setLaunchOpen(false)}
				onActivated={() => {
					enable('storefront').catch(() => {});
					markStorefrontLaunched();
				}}
			/>
		</>
	);
}
